/**
 * PositionTrainerAdapter
 *
 * Single-line walker for Position Trainer Battle (PTB). Each puzzle row
 * carries a space-separated UCI line in `moves`:
 *
 *     moves = "e8d7 a2e6 d7d8 f7f8"
 *              │opp setup│player ply 1│opp reply│player ply 2 (final)│
 *
 * Board rendering: we mount `ChessboardWithArrow` (the same wrapper the
 * standalone Position Trainer uses at src/Screens/Puzzles/index.tsx). It
 * provides piece rendering, promotion picker, possible-moves overlay,
 * king-in-check glow, click + drag, and last-move highlight without us
 * having to reinvent any of it.
 *
 * Integration contract with the wrapper:
 *   - The wrapper owns its internal chess.js instance and reloads it from
 *     the `position` (FEN) prop on every change. So we drive the board
 *     entirely via `position` (our own walker's FEN).
 *   - The wrapper calls `onPieceDropExtension(from, to, promo)` BEFORE
 *     executing any move it has already vetted as LEGAL. Returning `true`
 *     STOPS the wrapper from advancing its internal chess — we then push
 *     our own walker forward and pass a new `position` next render. This
 *     way our undo-on-wrong logic is replaced by "never let the wrapper
 *     mutate; always control position from above".
 *   - Click-to-move is handled inside the wrapper (its own selectedSquare
 *     state), so we don't carry a selectedFromRef anymore.
 *
 * Walker logic:
 *   - At puzzle load: spawn `new Chess(puzzle.fen)`, push moves[0] to reach
 *     the puzzle position. Set plyIndex = 1. Board orientation = side to
 *     move after the setup.
 *   - On the wrapper's pre-move hook: validate the UCI against
 *     moves[plyIndex].
 *       wrong  → onMove(false, "<puzzleId>:<uci>:0") + advance puzzle.
 *       correct + final ply → onMove(true, ...) + advance.
 *       correct + more line → advance walker.game with the player ply,
 *         schedule the opponent reply after 300 ms.
 *   - Illegal moves never reach us — the wrapper rejects them via its own
 *     `chess.moves({verbose: true})` check.
 *
 * Self-registers in the FunChallenges registry on import.
 */

import React, {useEffect, useRef, useState} from 'react';
import {StyleSheet, Text, View} from 'react-native';

import {Chess} from 'chess.js';

import CCColors from '@/Utils/CCColors';
import {resolvePuzzleMoves} from '@/Utils/puzzleEncrypter';
import useBattleSoundEffects from '@/Hooks/FunChallengesSounds/useBattleSoundEffects';
import useChessboardColorTheme from '@/Hooks/ChessboardColorTheme/useChessboardColorTheme';

import {BattleType, type BattlePuzzle} from '../types';
import {registerAdapter, type BattleAdapter} from '../registry';
// TRIAL (2026-07-09): swap the royale board to the NEW chessground-backed board
// used by PlayChess gameplay (@/Components/Chessboard, web via index.web.tsx —
// its prop surface + imperative ref API are documented as identical to the
// legacy wrapper). If anything misbehaves in testing (drag/commit, last-move
// highlight, orientation, or the onPieceDropExtension "truthy = block" path),
// revert by re-enabling the legacy import below and removing the new one.
// import ChessboardWithArrow from '@/Components/ChessboardWithArrow'; // legacy (revert target)
import ChessboardWithArrow from '@/Components/Chessboard';

export interface PositionTrainerAdapterProps {
  coords: string[];                              // puzzle ids as strings
  myIndex: number;                               // store's current puzzle slot
  boardWidth: number;
  puzzles: Record<string, BattlePuzzle>;
  onMove: (correct: boolean, square: string) => void;
  disabled?: boolean;
  /**
   * Hide the "Puzzle N of M" subtitle in the header pill (and the empty-
   * state). Used by Puzzle Dash Battle where the sequence is padded +
   * recycled and the absolute puzzle count isn't a meaningful signal —
   * the race progress chip in ScorePanel carries that information instead.
   * PTB leaves this off so the counter still renders.
   */
  hideCounter?: boolean;
  /**
   * Hide the whole "White/Black to move" header pill. Puzzle Royale shows the
   * side-to-move in its own status strip (RoyaleStrip), so the duplicate pill
   * above the board is suppressed there. PTB/PDB leave this off (header shows).
   */
  hidePrompt?: boolean;
  /**
   * Puzzle Royale spectate: fired on EVERY walker advance with the resulting
   * FEN, the move's from/to, the followed player's board ORIENTATION (locked
   * per puzzle — the player's side, so the spectator board doesn't flip with
   * side-to-move), and a `kind`:
   *   'setup'  — a new puzzle just loaded (the opponent's setup ply). The
   *              spectator holds the prior puzzle's final position briefly
   *              before applying this, so the solved puzzle is visible (#3).
   *   'player' — the followed player's own correct ply.
   *   'reply'  — the auto-played opponent reply.
   *   'wrong'  — the followed player's WRONG move. We DON'T mutate our own
   *              walker on a wrong move (the player's board snaps back), but we
   *              still stream the wrong position so spectators SEE the mistake
   *              before the next puzzle loads (#4). Applied immediately by the
   *              spectate view, then held by the next 'setup'.
   * The battle screen forwards these to the server → spectators.
   */
  onPly?: (
    fen: string,
    from: string,
    to: string,
    orientation: 'white' | 'black',
    kind: 'setup' | 'player' | 'reply' | 'wrong',
  ) => void;
}

const OPPONENT_REPLY_DELAY_MS = 300;

// ── Opponent setup-move animation (feature flag) ──────────────────────────
// Every non-`no_setup` puzzle opens with the OPPONENT's move already made
// (`plies[0]`, applied in buildWalker). Historically that move was applied
// before the board ever rendered, so the player was dropped straight onto the
// post-setup position — the move was over before it was visible, and the only
// cue for "which side am I?" was the last-move highlight plus the header pill.
//
// With this ON, the board first paints the PRE-setup position, then plays the
// opponent's move as a real animated move after OPP_MOVE_ANIMATE_DELAY_MS. The
// player watches the opponent move, which makes their own colour obvious.
//
// Turn OFF to restore the original instant-post-setup behaviour exactly.
export const OPP_MOVE_ANIMATE = true;
// How long the pre-setup position is held before the opponent's move plays.
// Tune to taste. Reference points: the mid-line opponent reply above uses
// 300ms and reads as a deliberate move; below ~150ms the eye tends to miss it
// entirely (a few frames), and much above ~600ms starts to feel like a stall —
// which matters in Puzzle Royale, where the race clock is server-authoritative
// and this hold is spent out of the player's own time.
export const OPP_MOVE_ANIMATE_DELAY_MS = 300;
// Slide duration for that one move. The board otherwise runs at 10ms (snap);
// a setup move that snaps is invisible, which would defeat the point.
export const OPP_MOVE_ANIMATE_SLIDE_MS = 10;

interface PuzzleWalker {
  /** Live chess.js instance (mutated in place as moves play). */
  game: Chess;
  /** Space-split UCI line. moves[0] = opponent setup. */
  plies: string[];
  /** Next ply expected: odd = player's, even = opponent's auto-reply. */
  plyIndex: number;
  /** The opponent's setup move from/to — drawn as the wrapper's lastMove. */
  lastMove: {from: string; to: string} | null;
  /** Position before the most recent walker advance. Drives the wrapper's
   *  last-move highlight (`lastMoveFen` prop). */
  previousFen: string | null;
  /** Board orientation: side to move AFTER setup is the player's side. */
  orientation: 'white' | 'black';
  /**
   * OPP_MOVE_ANIMATE only. The position BEFORE the opponent's setup move, held
   * on screen for OPP_MOVE_ANIMATE_DELAY_MS so that move can then play as a
   * visible animation. Null for `no_setup` puzzles (no setup ply exists) and
   * whenever the flag is off — in both cases the board renders the post-setup
   * position immediately, exactly as before.
   *
   * NOTE: `game`/`plyIndex`/`orientation` are ALREADY post-setup even while
   * this is being shown. Only the rendered FEN is held back — orientation is
   * derived from the post-setup position either way, so the board never flips
   * mid-animation, and scoring/validation are untouched by this flag.
   */
  pendingSetupFen: string | null;
}

function parseUciFromTo(uci: string): {from: string; to: string; promo?: string} | null {
  if (!uci || uci.length < 4) return null;
  return {
    from: uci.slice(0, 2),
    to: uci.slice(2, 4),
    promo: uci.length > 4 ? uci.slice(4, 5) : undefined,
  };
}

function buildWalker(puzzle: BattlePuzzle): PuzzleWalker | null {
  if (!puzzle || !puzzle.fen) return null;

  // The solution arrives obfuscated (Utils/puzzleEncrypter.ts). Resolve it HERE
  // — at use, once per puzzle — rather than decoding the whole set at fetch, so
  // the answers never sit in plaintext in the react-query cache (which is
  // cacheTime: Infinity and readable from devtools). Costs ~0.013ms.
  // resolvePuzzleMoves falls back to the plaintext `moves` field when no token
  // is present, so an older server keeps working unchanged.
  const line = resolvePuzzleMoves(puzzle);
  if (!line) return null;

  let game: Chess;
  try {
    game = new Chess(puzzle.fen);
  } catch {
    return null;
  }

  const plies = line.split(/\s+/).filter(Boolean);

  // ── no_setup (classroom curated / Demo-Call) ──────────────────────────────
  // The FEN is ALREADY the player's turn and moves[0] is the PLAYER's move —
  // there is NO opponent setup ply to auto-play. So we do NOT push moves[0];
  // plyIndex starts at 0 (the first expected ply is the player's), orientation
  // is the FEN's side-to-move, and no last-move highlight is drawn. A single
  // player move (plies.length === 1, e.g. a mate-in-1) is a valid puzzle here,
  // so only reject an empty line. Everything downstream (isFinalPlayerPly,
  // opponent-reply scheduling at even plyIndex) already works from plyIndex.
  if (puzzle.no_setup) {
    if (plies.length < 1) return null;
    const fenParts0 = game.fen().split(' ');
    const turn0 = fenParts0[1] === 'b' ? 'black' : 'white';
    return {
      game,
      plies,
      plyIndex: 0,
      lastMove: null,
      previousFen: null,
      orientation: turn0,
      // No setup ply to animate — the FEN is already the player's turn.
      pendingSetupFen: null,
    };
  }

  if (plies.length < 2) {
    // No player ply — the line is just the opponent's setup. Treat as
    // un-solvable; caller will skip / advance.
    return null;
  }

  // FEN before the opponent's setup move is pushed. Used as `lastMoveFen`
  // so the wrapper highlights the setup as a "last move" tinted overlay.
  const preSetupFen = game.fen();

  // Push the opponent's setup move (plies[0]). The resulting side-to-move
  // is the player's colour.
  const setup = parseUciFromTo(plies[0]);
  if (!setup) return null;
  let setupOk: ReturnType<Chess['move']> | null = null;
  try {
    setupOk = game.move({from: setup.from, to: setup.to, promotion: 'q'});
  } catch {
    setupOk = null;
  }
  if (!setupOk) return null;

  const fenParts = game.fen().split(' ');
  const turn = fenParts[1] === 'b' ? 'black' : 'white';

  return {
    game,
    plies,
    plyIndex: 1,
    lastMove: {from: setup.from, to: setup.to},
    previousFen: preSetupFen,
    orientation: turn,
    // Flag ON: hold the pre-setup position on screen so the opponent's move can
    // then play as a visible animation (the component clears this on a timer).
    // Flag OFF: null → the post-setup position renders immediately, unchanged.
    pendingSetupFen: OPP_MOVE_ANIMATE ? preSetupFen : null,
  };
}

function isFinalPlayerPly(walker: PuzzleWalker): boolean {
  // After this player ply, are there any plies left? If no → final.
  return walker.plyIndex >= walker.plies.length - 1;
}

function pliesMatch(expectedUci: string, attemptedUci: string): boolean {
  if (!expectedUci || !attemptedUci) return false;
  if (expectedUci === attemptedUci) return true;
  // Promotion forgiveness: if the player drags without explicit promotion
  // letter and the expected ply auto-promotes to queen.
  if (expectedUci.length === 5 && expectedUci.startsWith(attemptedUci)) {
    return expectedUci.endsWith('q');
  }
  return false;
}

function PositionTrainerAdapterViewInner(props: PositionTrainerAdapterProps) {
  const {
    coords,
    myIndex,
    boardWidth,
    puzzles,
    onMove,
    disabled = false,
    hideCounter = false,
    hidePrompt = false,
    onPly,
  } = props;

  const currentId =
    coords.length > 0 ? coords[myIndex % coords.length] : '';
  const puzzle = currentId ? puzzles[currentId] : undefined;

  const walkerRef = useRef<PuzzleWalker | null>(null);
  const [, forceTick] = useState(0);
  const tick = () => forceTick(t => t + 1);

  // Pending "reveal the opponent's setup move" timer (OPP_MOVE_ANIMATE).
  const setupRevealRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // True only while the setup move is sliding — lifts the board's
  // animationDuration for that one move so it's actually visible.
  const setupSlidingRef = useRef(false);

  // Rebuild the walker whenever the puzzle id we're on changes.
  useEffect(() => {
    // A reveal timer from the PREVIOUS puzzle must never land on the new
    // walker: it would clear a pendingSetupFen that belongs to a different
    // position, skipping the new puzzle's animation.
    if (setupRevealRef.current) {
      clearTimeout(setupRevealRef.current);
      setupRevealRef.current = null;
    }
    setupSlidingRef.current = false;

    walkerRef.current = puzzle ? buildWalker(puzzle) : null;

    // NOTE: the reveal timer is NOT armed here — it's armed by the
    // playable-gated effect below. See the comment there.

    // Spectate stream: emit the new puzzle's position so followers jump to the
    // puzzle the player is now on. For a normal puzzle we animate the opponent
    // setup move (lastMove); for a no_setup puzzle there IS no setup move, so we
    // still emit the initial position (empty from/to = no animated move) so the
    // spectator board loads the new puzzle.
    const w = walkerRef.current;
    if (onPly && w) {
      if (w.lastMove) {
        onPly(w.game.fen(), w.lastMove.from, w.lastMove.to, w.orientation, 'setup');
      } else {
        onPly(w.game.fen(), '', '', w.orientation, 'setup');
      }
    }
    tick();
  }, [puzzle?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const opponentTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {playCorrect, playWrong} = useBattleSoundEffects();
  const chessboardColorTheme = useChessboardColorTheme();

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      if (opponentTimeoutRef.current) clearTimeout(opponentTimeoutRef.current);
      if (setupRevealRef.current) clearTimeout(setupRevealRef.current);
    };
  }, []);

  // Clear pending opponent timer when the game disables.
  useEffect(() => {
    if (!disabled) return;
    if (opponentTimeoutRef.current) clearTimeout(opponentTimeoutRef.current);
    // Board went dead (3-2-1 countdown, battle over, paused). Cancel any
    // in-flight reveal but DON'T clear `pendingSetupFen` — the hold stays, and
    // the effect below re-arms the timer from the top once the board is
    // playable again. Snapping here would burn the animation behind the
    // countdown overlay (see that effect's comment).
    if (setupRevealRef.current) {
      clearTimeout(setupRevealRef.current);
      setupRevealRef.current = null;
    }
  }, [disabled]);

  // ── Arm the setup-move reveal ONLY while the board is playable ────────────
  // The delay must be counted from when the player can actually SEE and act on
  // the board, not from when the puzzle mounted. In Puzzle Royale the first
  // puzzle mounts BEHIND the 3-2-1 countdown overlay with `disabled` true; a
  // timer armed at mount would fire during the countdown, so the one puzzle
  // where "which colour am I?" matters most would be the one puzzle whose
  // opponent move was never visible. Gating on `disabled` makes the sequence
  // 3-2-1 → Go! → hold → opponent moves → your turn, and re-arms correctly
  // after any other pause too.
  useEffect(() => {
    if (disabled) return;
    const built = walkerRef.current;
    if (!built || !built.pendingSetupFen) return;
    if (setupRevealRef.current) return; // already counting down
    setupRevealRef.current = setTimeout(() => {
      setupRevealRef.current = null;
      // Re-read the ref: the puzzle may have changed while we waited.
      const cur = walkerRef.current;
      if (!cur || cur !== built) return;
      cur.pendingSetupFen = null;
      setupSlidingRef.current = true;
      tick();
      // Drop back to the snappy 10ms board once this move has landed, so the
      // player's own moves keep their instant feel.
      setTimeout(() => {
        setupSlidingRef.current = false;
      }, OPP_MOVE_ANIMATE_SLIDE_MS);
    }, OPP_MOVE_ANIMATE_DELAY_MS);
    // `puzzle?.id` is a dep so a NEW puzzle arriving while already enabled
    // (the common mid-race case) arms its own reveal — `disabled` alone
    // wouldn't change between puzzles.
  }, [disabled, puzzle?.id]);

  /**
   * Called by the wrapper BEFORE it advances its internal chess instance.
   * Returning `true` STOPS the wrapper (per chessboard-package contract
   * in node_modules/chessboard-package/src/components/ChessGame.js:158-164).
   *
   * We always return true — we drive position ourselves via the walker.
   * The wrapper has already validated legality before calling us, so any
   * (from, to, promo) here is a legal move; the only question is whether
   * it matches the puzzle's expected ply.
   */
  const onPieceDropExtension = (
    from: string,
    to: string,
    promo?: string,
  ): boolean => {
    if (disabled) return true; // STOP regardless
    const walker = walkerRef.current;
    if (!walker || !currentId) return true;
    // The opponent's setup move hasn't played yet — the board on screen is the
    // PRE-setup position. Swallow input: the player is looking at a position
    // that is about to change, and `walker.game` is already post-setup, so a
    // drop here would be validated against a position they can't see and
    // mis-scored as wrong. Board is non-draggable during this window too (see
    // the `disabled` prop below); this is the belt-and-braces half.
    if (walker.pendingSetupFen) return true;

    const puzzleIdNum = parseInt(currentId, 10);
    if (!Number.isFinite(puzzleIdNum)) return true;

    // chessboard-package's onDrop derives `promotion` from the SECOND CHAR
    // of the dragged piece's code (e.g. "wN" → "n"), regardless of whether
    // the move actually promotes — see
    // node_modules/chessboard-package/src/components/ChessGame.js:140-148.
    // For click-to-move it passes nothing, so we get undefined.
    //
    // The puzzle line uses the standard Lichess UCI convention: a 5-char
    // string ONLY for actual promotions (e.g. "e7e8q"), 4 chars otherwise.
    // Filter the spurious promo letter unless the move is actually a pawn
    // reaching rank 1 / 8.
    const sourcePiece = walker.game.get(from as any);
    const isPawn = sourcePiece && sourcePiece.type === 'p';
    const destRank = to.length === 2 ? to[1] : '';
    const isPromotionRank =
      (sourcePiece?.color === 'w' && destRank === '8') ||
      (sourcePiece?.color === 'b' && destRank === '1');
    const effectivePromo =
      promo && /^[qrbn]$/i.test(promo) && isPawn && isPromotionRank
        ? promo.toLowerCase()
        : '';

    const playerUci = from + to + effectivePromo;
    const expectedUci = walker.plies[walker.plyIndex] || '';

    if (!pliesMatch(expectedUci, playerUci)) {
      // Wrong move for the puzzle. Don't touch walker.game — we returned
      // STOP so the wrapper's internal chess didn't advance either, and
      // our position prop is unchanged (the player's board snaps back).
      // BUT still STREAM the wrong move to spectators (#4): compute the
      // post-wrong-move FEN on a CLONE (so our own walker stays put) and emit
      // it as kind:'wrong' so followers see the mistake before the next puzzle.
      if (onPly) {
        try {
          const clone = new Chess(walker.game.fen());
          const wrongMove = clone.move({
            from,
            to,
            promotion: effectivePromo || undefined,
          });
          if (wrongMove) {
            onPly(clone.fen(), from, to, walker.orientation, 'wrong');
          }
        } catch {
          // Illegal on the clone (shouldn't happen — wrapper pre-validated);
          // skip the stream rather than crash.
        }
      }
      playWrong();
      walkerRef.current = null; // freeze until parent advances myIndex
      onMove(false, `${puzzleIdNum}:${playerUci}:0`);
      return true;
    }

    // Correct ply — advance our walker. Use the ACTUAL chosen promotion piece
    // (effectivePromo), NOT a hardcoded queen — else an under-promotion (the
    // puzzle's expected ...n/...r/...b) was validated by UCI string-match but
    // then applied to the board as a QUEEN, diverging the FEN from the puzzle
    // line → the next ply became illegal and the board froze. `undefined` lets
    // chess.js handle non-promotion moves normally.
    let move: ReturnType<Chess['move']> | null = null;
    try {
      const before = walker.game.fen();
      move = walker.game.move({
        from,
        to,
        promotion: effectivePromo || undefined,
      });
      if (move) walker.previousFen = before;
    } catch {
      move = null;
    }
    if (!move) {
      // Should never happen — the wrapper already verified legality — but
      // bail safely just in case.
      return true;
    }
    playCorrect();
    walker.lastMove = {from, to};
    // Spectate stream: emit this player ply so followers' boards animate it.
    if (onPly) {
      onPly(walker.game.fen(), from, to, walker.orientation, 'player');
    }

    if (isFinalPlayerPly(walker)) {
      walkerRef.current = null;
      onMove(true, `${puzzleIdNum}:${playerUci}:1`);
      return true;
    }

    // More line to go: auto-play opponent's reply after a 300 ms beat.
    walker.plyIndex += 1;
    const opponentUci = walker.plies[walker.plyIndex] || '';
    const opp = parseUciFromTo(opponentUci);
    if (opp) {
      opponentTimeoutRef.current = setTimeout(() => {
        try {
          const before = walker.game.fen();
          const replyMove = walker.game.move({
            from: opp.from,
            to: opp.to,
            // Use the opponent ply's actual promotion piece (the line may
            // under-promote), not a queen-only check.
            promotion: opp.promo || undefined,
          });
          if (replyMove) {
            walker.previousFen = before;
            walker.lastMove = {from: opp.from, to: opp.to};
            walker.plyIndex += 1;
            // Spectate stream: emit the auto-played reply too.
            if (onPly) {
              onPly(walker.game.fen(), opp.from, opp.to, walker.orientation, 'reply');
            }
          }
        } catch {
          // Malformed line mid-puzzle — silently skip; the timer will
          // eventually advance the puzzle.
        }
        tick();
      }, OPPONENT_REPLY_DELAY_MS);
    }
    tick();
    return true;
  };

  if (!coords.length) {
    return (
      <View style={[styles.empty, {width: boardWidth, height: boardWidth}]}>
        <Text style={styles.emptyText}>Loading battle…</Text>
      </View>
    );
  }

  if (!puzzle) {
    return (
      <View style={[styles.empty, {width: boardWidth, height: boardWidth}]}>
        <Text style={styles.emptyText}>Loading puzzle…</Text>
        {!hideCounter && (
          <Text style={styles.emptySub}>
            Puzzle {(myIndex % coords.length) + 1} of {coords.length}
          </Text>
        )}
      </View>
    );
  }

  if (!walkerRef.current) {
    return (
      <View style={[styles.empty, {width: boardWidth, height: boardWidth}]}>
        <Text style={styles.emptyText}>Next puzzle…</Text>
      </View>
    );
  }

  const walker = walkerRef.current;
  const promptText =
    walker.orientation === 'white' ? 'White to move' : 'Black to move';
  const promptSub = hideCounter
    ? ''
    : `Puzzle ${(myIndex % coords.length) + 1} of ${coords.length}`;

  return (
    <View style={styles.adapterRoot}>
      {!disabled && !hidePrompt && (
        <View style={[styles.headerBar, {width: boardWidth}]}>
          <View style={styles.headerPill}>
            <Text style={styles.headerText}>{promptText}</Text>
            {!hideCounter && (
              <>
                <View style={styles.headerDot} />
                <Text style={styles.headerSub}>{promptSub}</Text>
              </>
            )}
          </View>
        </View>
      )}
      {/*
        The wrapper measures its container with a ResizeObserver and renders
        a square that fills `width: 100%`. Constrain to the parent's
        chosen boardWidth so the rest of the BattleScreen layout still
        works.
       */}
      <View style={{width: boardWidth, alignSelf: 'center'}}>
        <ChessboardWithArrow
          // OPP_MOVE_ANIMATE: while the setup move is pending we render the
          // PRE-setup fen; when the timer clears it the position prop flips to
          // the post-setup fen and the board animates the difference — which is
          // exactly the opponent's move. `walker.game` is post-setup throughout,
          // so this is purely a display hold.
          position={walker.pendingSetupFen ?? walker.game.fen()}
          // No last-move tint while holding: the setup move hasn't "happened"
          // on screen yet, and highlighting its destination square early gives
          // the move away before it plays.
          lastMoveFen={
            walker.pendingSetupFen ?? walker.previousFen ?? walker.game.fen()
          }
          boardOrientation={walker.orientation}
          onPieceDropExtension={onPieceDropExtension}
          colorTheme={chessboardColorTheme}
          // NEW board (@/Components/Chessboard) uses `disabled`, not
          // `arePiecesDraggable`. draggable = !disabled → disabled.
          // (Legacy revert: replace with `arePiecesDraggable={!disabled}`.)
          // Also non-draggable while the setup move is pending — the position
          // on screen is about to change under the player's hand.
          disabled={disabled || !!walker.pendingSetupFen}
          // Snap pieces to their destination instead of the default
          // 300ms slide. Matches the PlayChess board and the standalone
          // Position Trainer's feel; with our auto-played opponent reply
          // 300ms after a correct move, the slide animation otherwise
          // doubles up and feels laggy.
          //
          // The one exception is the opponent's setup move (OPP_MOVE_ANIMATE):
          // a 10ms snap would be invisible, defeating the point of holding the
          // position at all, so that single move slides.
          animationDuration={
            setupSlidingRef.current ? OPP_MOVE_ANIMATE_SLIDE_MS : 10
          }
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  adapterRoot: {
    alignSelf: 'center',
    alignItems: 'center',
    gap: 12,
  },
  empty: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    alignSelf: 'center',
    backgroundColor: '#F3EFE7',
  },
  emptyText: {
    fontSize: 16,
    fontWeight: '700',
    color: '#262322',
  },
  emptySub: {
    fontSize: 13,
    color: '#4D3F37',
  },
  headerBar: {
    alignSelf: 'center',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: 'rgba(61, 171, 158, 0.96)',
    borderRadius: 999,
    paddingHorizontal: 18,
    paddingVertical: 8,
    shadowColor: '#000',
    shadowOpacity: 0.12,
    shadowRadius: 6,
    shadowOffset: {width: 0, height: 3},
    elevation: 3,
  },
  headerText: {
    fontWeight: '800',
    color: CCColors.White,
    fontSize: 15,
    letterSpacing: 0.3,
  },
  headerDot: {
    width: 4,
    height: 4,
    borderRadius: 2,
    backgroundColor: 'rgba(255,255,255,0.55)',
  },
  headerSub: {
    color: CCColors.White,
    fontSize: 13,
    fontWeight: '600',
    opacity: 0.94,
  },
});

/**
 * Memo the view: BattleScreen re-renders every 200 ms (its `secondsLeft`
 * countdown). That re-render is innocuous for the score panel but lethal
 * for the chessboard-package wrapper underneath — its inner
 * `customSquareStyles` is rebuilt fresh on every render, and the
 * selected-square / possible-moves highlights are applied via a
 * post-paint useEffect (see node_modules/chessboard-package/src/components/
 * Chessboard.js:410-422). On any re-render that DIDN'T originate from a
 * `selectedSquare`/`possibleMoves` state change, that useEffect doesn't
 * fire, so the freshly-built squareStyles ships without the green dots —
 * the user sees them flash once on click then disappear on the very next
 * 200 ms tick.
 *
 * React.memo with Object.is dep check is enough — our props (`coords`,
 * `myIndex`, `boardWidth`, `puzzles`, `onMove`, `disabled`) all have
 * stable identity across `secondsLeft` ticks. `onMove` is `useCallback`'d
 * in BattleScreen; `puzzles` is stabilised there too (see the
 * useMemo around `useBattlePuzzles().data?.puzzles`).
 */
export const PositionTrainerAdapterView = React.memo(
  PositionTrainerAdapterViewInner,
);

const adapter: BattleAdapter = {
  battleType: BattleType.POSITION_TRAINER,
  renderGame: (props: PositionTrainerAdapterProps) => (
    <PositionTrainerAdapterView {...props} />
  ),
};

registerAdapter(adapter);

export default adapter;
